import { AJUDA_CATALOGO } from "@/lib/ajuda/dialogos/catalogo";
import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@bookdental/ui";
import { CATEGORIAS_DE_SERVICO, MAX_DESCRICAO_DO_SERVICO, type AbrangenciaDoServico } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { FichaTecnicaTab } from "./FichaTecnicaTab";
import { ServicoMateriaisECores, escalasJaDeclaradas } from "./ServicoMateriaisECores";
import { CampoAbrangencia, CampoCategoria, NOVA_CATEGORIA } from "./CampoCategoria";
import { CabecalhoDoServico } from "./servico/CabecalhoDoServico";
import { AbasDoServico, type AbaDoServico } from "./servico/AbasDoServico";
import { RodapeDoServico } from "./servico/RodapeDoServico";
import { useSalvarServico } from "./servico/use-salvar-servico";

const inputCls =
  "w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground";
const labelCls = "mb-1 block text-xs font-medium text-zinc-600 dark:text-muted-foreground";

export type ServiceCategoryEditTarget = {
  id: string;
  label: string;
  group: string | null;
  abrangencia?: string | null;
  descricao?: string | null;
  /** A lista só diz se tem foto; a foto em si vem por `imagemDoServico`. */
  imagem?: { atualizadoEm: Date | string } | null;
  organizationId: string | null;
  compatibleMaterialOptions: Array<{ materialOption: { id: string } }>;
};

/** O serviço do Catálogo como um perfil (pedido do Jun): foto, categoria, nome e descrição no topo;
 *  embaixo os dados e os materiais, ou a ficha técnica. */
export function ServiceCategoryDialog({
  open,
  onClose,
  editTarget,
  abaInicial,
  fichaInicial,
}: {
  open: boolean;
  onClose: () => void;
  /** Presente = modo edição. Todo item da lista já é do laboratório — não há mais fork. */
  editTarget?: ServiceCategoryEditTarget | null;
  /** Quem abriu pela lista de fichas já quer a ficha na tela, não os dados do serviço. */
  abaInicial?: AbaDoServico;
  /** E quer aquela ficha, não a primeira do serviço. */
  fichaInicial?: { id: string; materialOptionId: string | null };
}) {
  const coresQuery = trpc.prosthesis.catalog.myShadeOptions.useQuery();
  const imagemQuery = trpc.prosthesis.catalog.imagemDoServico.useQuery(
    { serviceCategoryId: editTarget?.id ?? "" },
    { enabled: open && !!editTarget?.imagem },
  );
  const [label, setLabel] = useState("");
  const [group, setGroup] = useState<string>(CATEGORIAS_DE_SERVICO[0]);
  const [novaCategoria, setNovaCategoria] = useState("");
  const [abrangencia, setAbrangencia] = useState<AbrangenciaDoServico>("DENTE");
  const [descricao, setDescricao] = useState("");
  /** undefined = a foto que já está salva; o resto é o que a pessoa escolheu agora. */
  const [imagemNova, setImagemNova] = useState<string | null | undefined>(undefined);
  const [materialOptionIds, setMaterialOptionIds] = useState<string[]>([]);
  /** Só os materiais que a pessoa tocou vão pro servidor — o que não está aqui fica como estava. */
  const [escalasPorMaterial, setEscalasPorMaterial] = useState<Record<string, string[]>>({});
  const [aba, setAba] = useState<AbaDoServico>("dados");
  const { salvar, remover, salvando, excluindo } = useSalvarServico();

  const isEdit = !!editTarget;
  /** As cores só se semeiam uma vez por abertura. Sem isso, um refetch de `myShadeOptions` com o
   *  diálogo aberto (troca de aba do navegador basta) reexecutaria o efeito e apagaria as escalas
   *  que a pessoa acabou de marcar. */
  const semeouCores = useRef(false);

  useEffect(() => {
    if (!open) {
      semeouCores.current = false;
      return;
    }
    setNovaCategoria("");
    setImagemNova(undefined);
    setEscalasPorMaterial({});
    setLabel(editTarget?.label ?? "");
    setGroup(editTarget ? editTarget.group || "Outros" : CATEGORIAS_DE_SERVICO[0]);
    setAbrangencia(editTarget?.abrangencia === "ARCADA" ? "ARCADA" : "DENTE");
    setDescricao(editTarget?.descricao ?? "");
    setMaterialOptionIds(editTarget?.compatibleMaterialOptions.map((c) => c.materialOption.id) ?? []);
    setAba(editTarget ? (abaInicial ?? "dados") : "dados");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editTarget?.id, abaInicial]);

  useEffect(() => {
    if (!open || !editTarget || semeouCores.current || !coresQuery.data) return;
    setEscalasPorMaterial(escalasJaDeclaradas(coresQuery.data as any));
    semeouCores.current = true;
  }, [open, editTarget, coresQuery.data]);

  const categoria = group === NOVA_CATEGORIA ? novaCategoria.trim() : group;
  const imagem = imagemNova !== undefined ? imagemNova : editTarget?.imagem ? (imagemQuery.data ?? null) : null;

  async function handleSave() {
    const ok = await salvar(editTarget?.id ?? null, {
      label,
      group: categoria,
      abrangencia,
      descricao,
      materialOptionIds,
      escalasPorMaterial,
      imagem: imagemNova,
    });
    if (ok) onClose();
  }

  async function handleExcluir() {
    if (!editTarget || !window.confirm(`Excluir o serviço "${editTarget.label}"?`)) return;
    if (await remover(editTarget.id)) onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      {/* A ficha é uma tabela com cinco colunas de número: na aba de ficha o diálogo abre mais largo. */}
      <DialogContent
        className={cn(
          "flex max-h-[90dvh] flex-col gap-4 overflow-y-auto border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card",
          isEdit && aba === "ficha" ? "max-w-3xl" : "max-w-2xl",
        )}
      >
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_CATALOGO.servicoDoLab}>{isEdit ? "Editar serviço" : "Novo serviço"}</DialogTitle>
        </DialogHeader>

        <CabecalhoDoServico
          imagem={imagem}
          carregando={imagemNova === undefined && !!editTarget?.imagem && imagemQuery.isLoading}
          categoria={categoria}
          nome={label}
          descricao={descricao}
          onImagem={setImagemNova}
        />

        {/* A ficha é a receita DESTE serviço e precisa de um id pra pendurar — por isso a aba só
            existe na edição. Quem está criando ainda não tem serviço nenhum. */}
        {isEdit && <AbasDoServico aba={aba} onAba={setAba} />}

        {isEdit && aba === "ficha" && editTarget ? (
          <FichaTecnicaTab
            // Remonta ao trocar de ficha pela lista: o estado interno (linhas digitadas, ficha
            // escolhida) é do par anterior e não deve sobreviver à troca.
            key={fichaInicial?.id ?? "primeira"}
            serviceCategoryId={editTarget.id}
            serviceLabel={editTarget.label}
            materialOptionIds={materialOptionIds}
            fichaInicial={fichaInicial}
          />
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <CampoCategoria valor={group} novaCategoria={novaCategoria} onChange={setGroup} onNovaCategoria={setNovaCategoria} />
              <CampoAbrangencia valor={abrangencia} onChange={setAbrangencia} />
            </div>

            <div>
              <p className={labelCls}>Nome do serviço</p>
              <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Ex: Coroa especial V2" className={cn(inputCls, "h-10")} />
            </div>

            <div>
              <p className={labelCls}>Descrição (opcional)</p>
              <div className="relative">
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value.slice(0, MAX_DESCRICAO_DO_SERVICO))}
                  rows={2}
                  placeholder="O que é o serviço e para que serve — o dentista lê isso no catálogo."
                  className={cn(inputCls, "resize-none py-2 pb-6")}
                />
                <span className="pointer-events-none absolute bottom-1.5 right-2.5 text-[11px] tabular-nums text-zinc-400">
                  {descricao.length}/{MAX_DESCRICAO_DO_SERVICO}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-zinc-200 p-4 dark:border-border">
              <ServicoMateriaisECores
                materialOptionIds={materialOptionIds}
                onMaterialOptionIds={setMaterialOptionIds}
                escalasPorMaterial={escalasPorMaterial}
                onEscalasPorMaterial={setEscalasPorMaterial}
              />
            </div>

            <RodapeDoServico
              editando={isEdit}
              salvando={salvando}
              podeSalvar={label.trim().length >= 2 && categoria.length >= 2}
              excluindo={excluindo}
              onExcluir={() => void handleExcluir()}
              onCancelar={onClose}
              onSalvar={() => void handleSave()}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
